import { useEffect, useMemo, useState } from "react";
import { fetchAvailableTags } from "../../services/arthouseApi";

/**
 * Goals:
 * - AI works as an independent toggle (unchanged).
 * - Photography is its own category toggle (like AI), and:
 * - Photography posts should only show when "photography" is selected (your feed/API should enforce this key).
 * - When Photography is active, the rest of the tag row switches to Photography-only options.
 * - When Photography is NOT active, show normal art tags.
 * - Preserve multi-select behavior + maxSelected cap.
 */

const AI_TAG = { key: "ai", label: "AI Art" };
const PHOTO_TAG = { key: "photography", label: "Photography" };

/* -----------------------------
   Regular Art tags (default view)
----------------------------- */
const ART_TAGS = [
  { key: "all", label: "All" },

  // Origin / intent
  { key: "fanart", label: "Fan Art" },
  { key: "oc", label: "Original Character (OC)" },
  { key: "original", label: "Original Work" },

  // Finish level
  { key: "sketch", label: "Sketch" },
  { key: "fully-rendered", label: "Fully Rendered" },

  // Type / discipline
  { key: "illustration", label: "Illustration" },
  { key: "concept-art", label: "Concept Art" },
  { key: "character-design", label: "Character Design" },
  { key: "environment", label: "Environment" },

  // Style
  { key: "manga", label: "Manga Style" },
  { key: "chibi", label: "Chibi" },

  // Subject / framing
  { key: "portrait", label: "Portrait" },
  { key: "full-body", label: "Full Body" },
  { key: "couple-ship", label: "Couple / Ship" },
  { key: "group", label: "Group" },

  // Subjects
  { key: "mecha", label: "Mecha" },
  { key: "creature", label: "Creatures" },
];

/* -----------------------------
   Photography-only tags (shown only when Photography is active)
   Keep these scoped to photo so filters stay clean.
----------------------------- */
const PHOTO_TAGS = [
  { key: "all", label: "All" },

  // Genre / intent
  { key: "portrait-photo", label: "Portrait" },
  { key: "cosplay-photo", label: "Cosplay Shoot" },
  { key: "street-photo", label: "Street" },
  { key: "editorial-photo", label: "Editorial" },

  // Look / style
  { key: "cinematic-photo", label: "Cinematic" },
  { key: "bw-photo", label: "Black & White" },
  { key: "low-light-photo", label: "Low Light" },

  // Composition
  { key: "close-up-photo", label: "Close-up" },
  { key: "full-body-photo", label: "Full Body" },

  // Setting
  { key: "studio-photo", label: "Studio" },
  { key: "outdoor-photo", label: "Outdoor" },
];

function normalizeTagKey(input) {
  return String(input || "")
    .trim()
    .toLowerCase()
    .replace(/[’'"]/g, "")
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/-+/g, "-")
    .replace(/^-|-$/g, "");
}

function toTagArray(tag) {
  if (Array.isArray(tag)) return tag.map(normalizeTagKey).filter(Boolean);
  if (typeof tag === "string" && tag.trim()) {
    // allow legacy comma-separated string: "fanart,portrait"
    return tag
      .split(",")
      .map(normalizeTagKey)
      .filter(Boolean);
  }
  return [];
}

function serializeTags(tags) {
  return tags.join(",");
}

/**
 * Mode behavior:
 * - "photography" is a mode tag.
 * - When photography is active:
 * - only photography sub-tags are shown in the row
 * - switching off photography removes photo sub-tags too
 * - AI can be toggled on/off in either mode.
 *
 * NOTE: Your feed/API should treat "photography" as a hard gate so photo work
 * does not appear unless this tag is present.
 */
const PHOTO_MODE_KEYS = new Set([
  "portrait-photo",
  "cosplay-photo",
  "street-photo",
  "editorial-photo",
  "cinematic-photo",
  "bw-photo",
  "low-light-photo",
  "close-up-photo",
  "full-body-photo",
  "studio-photo",
  "outdoor-photo",
]);

export default function ArtFilters({ tag, setTag, maxSelected = 4 }) {
  const [availableTags, setAvailableTags] = useState([]);
  const [loadingTags, setLoadingTags] = useState(true);

  // Fetch available tags from API
  useEffect(() => {
    fetchAvailableTags()
      .then((data) => {
        const tags = Array.isArray(data?.tags) ? data.tags : [];
        setAvailableTags(tags);
      })
      .catch((err) => {
        console.error('Failed to fetch tags:', err);
        setAvailableTags([]);
      })
      .finally(() => {
        setLoadingTags(false);
      });
  }, []);

  const activeTags = useMemo(() => {
    const arr = toTagArray(tag);
    // if nothing selected, treat as "all" (UI only)
    return arr.length ? arr : ["all"];
  }, [tag]);

  const isAllActive = activeTags.includes("all");
  const isAiActive = activeTags.includes("ai");
  const isPhotoMode = activeTags.includes("photography");

  // Convert available tags to format expected by UI
  const dynamicTags = useMemo(() => {
    return availableTags.map((t) => ({
      key: normalizeTagKey(t.tag),
      label: t.tag,
      count: t.count,
    }));
  }, [availableTags]);

  // Filter out mode tags (ai, photography) from dynamic tags
  const contentTags = useMemo(() => {
    return dynamicTags.filter(
      (t) => t.key !== 'ai' && t.key !== 'photography'
    );
  }, [dynamicTags]);

  const visibleTags = isPhotoMode
    ? contentTags.filter((t) => PHOTO_MODE_KEYS.has(t.key) || t.key === 'all')
    : contentTags;

  function setTags(nextArr) {
    const cleaned = nextArr
      .map(normalizeTagKey)
      .filter(Boolean)
      .filter((v) => v !== "all");

    setTag(cleaned.length ? serializeTags(cleaned) : "");
  }

  function clearPhotoSubTags(tagsArr) {
    return tagsArr.filter((t) => !PHOTO_MODE_KEYS.has(t));
  }

  function toggleAi() {
    const current = activeTags.filter((t) => t !== "all");
    const has = current.includes("ai");

    const next = has ? current.filter((t) => t !== "ai") : [...current, "ai"];
    const uniq = Array.from(new Set(next));
    const limited = uniq.length > maxSelected ? uniq.slice(0, maxSelected) : uniq;
    setTags(limited);
  }

  function togglePhotographyMode() {
    const current = activeTags.filter((t) => t !== "all");
    const hasPhoto = current.includes("photography");

    if (hasPhoto) {
      // turning off photography should also remove photography-only sub tags
      const next = clearPhotoSubTags(current).filter((t) => t !== "photography");
      setTags(next);
      return;
    }

    // turning on photography keeps other tags if you want, but the UI will switch to photo filters.
    // IMPORTANT: To enforce "photo only shows when clicked", your feed/API should gate on "photography".
    // We add it as a tag here.
    const next = [...current, "photography"];
    const uniq = Array.from(new Set(next));
    const limited = uniq.length > maxSelected ? uniq.slice(0, maxSelected) : uniq;
    setTags(limited);
  }

  function toggleTag(rawKey) {
    const k = normalizeTagKey(rawKey);

    // "All" clears everything
    if (k === "all") {
      setTag("");
      return;
    }

    if (k === "ai") {
      toggleAi();
      return;
    }

    if (k === "photography") {
      togglePhotographyMode();
      return;
    }

    const current = activeTags.filter((t) => t !== "all");
    const has = current.includes(k);

    if (has) {
      setTags(current.filter((t) => t !== k));
      return;
    }

    if (current.length >= maxSelected) return;
    setTags([...current, k]);
  }

  return (
    <div className="w-full overflow-x-auto scrollbar-hide">
      <div className="flex items-center gap-3 px-1 py-2 min-w-max">
      {/* MODE TOGGLES (left) — sleek outlined “capsule boxes” */}
      <button
        onClick={() => toggleTag(PHOTO_TAG.key)}
        className={[
          "h-9 px-4 rounded-xl border text-[12.5px] font-semibold transition whitespace-nowrap",
          "bg-transparent",
          isPhotoMode
            ? "border-amber-300/55 text-amber-100 bg-amber-300/[0.06]"
            : "border-white/25 text-white/85 hover:border-white/45 hover:text-white",
        ].join(" ")}
      >
        {PHOTO_TAG.label}
      </button>

      <button
        onClick={() => toggleTag(AI_TAG.key)}
        className={[
          // outlined box look (more “mode switch” than “tag”)
          "h-9 px-4 rounded-xl border text-[12.5px] font-semibold transition whitespace-nowrap",
          "bg-transparent",
          // subtle cinematic outline + crisp hover
          isAiActive
            ? "border-violet-300/55 text-violet-100 bg-violet-300/[0.06]"
            : "border-white/25 text-white/85 hover:border-white/45 hover:text-white",
        ].join(" ")}
      >
        {AI_TAG.label}
      </button>

        {/* Divider */}
        <div className="h-6 w-px bg-white/15 mx-1 opacity-70" />

        {/* "All" button */}
        <button
          onClick={() => toggleTag('all')}
          className={[
            "h-8 px-4 rounded-full border text-[13px] font-medium transition whitespace-nowrap",
            isAllActive
              ? "border-emerald-300/45 bg-emerald-300/10 text-emerald-100"
              : "border-white/15 text-white/70 hover:border-white/30 hover:text-white",
          ].join(" ")}
        >
          All
        </button>

        {/* FILTER TAGS (right) — dynamic tags from API */}
        {loadingTags ? (
          <div className="text-sm text-white/50 px-2">Loading tags...</div>
        ) : visibleTags.length === 0 ? (
          <div className="text-sm text-white/50 px-2">No tags available</div>
        ) : (
          visibleTags.map((t) => {
            const key = normalizeTagKey(t.key);
            const nonAll = activeTags.filter((x) => x !== "all");
            const active = nonAll.includes(key);

            return (
              <button
                key={key}
                onClick={() => toggleTag(key)}
                className={[
                  "h-8 px-4 rounded-full border text-[13px] font-medium transition whitespace-nowrap",
                  active
                    ? "border-emerald-300/45 bg-emerald-300/10 text-emerald-100"
                    : "border-white/15 text-white/70 hover:border-white/30 hover:text-white",
                ].join(" ")}
                title={t.count ? `${t.count} artworks` : undefined}
              >
                {t.label}
                {t.count && active ? (
                  <span className="ml-1.5 text-[11px] opacity-60">({t.count})</span>
                ) : null}
              </button>
            );
          })
        )}
    </div>
  </div>
);

}
